import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { formatDate } from '../../utils/date';
import { toast } from 'sonner';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    filterBookings();
  }, [searchTerm, bookings]);

  const fetchBookings = async () => {
    try {
      const response = await api.get('/bookings');
      setBookings(response.data);
      setFilteredBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    if (!searchTerm) {
      setFilteredBookings(bookings);
      return;
    }
    const filtered = bookings.filter(
      (booking) =>
        booking.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.guest_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBookings(filtered);
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await api.put(`/bookings/${bookingId}`, { booking_status: status });
      toast.success('Booking updated successfully');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return '#8F9E8B';
      case 'cancelled':
        return '#C67D63';
      case 'completed':
        return '#1A3C34';
      default:
        return '#64748B';
    }
  };

  const getPaymentStatusColor = (status) => {
    return status === 'paid' ? '#8F9E8B' : '#C67D63';
  };

  return (
    <div className="flex" style={{ background: '#F9F8F6' }}>
      <AdminSidebar />
      <div className="admin-content flex-1" data-testid="admin-bookings">
        <div className="mb-8">
          <h1
            className="text-4xl font-bold mb-2"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
          >
            Bookings
          </h1>
          <p style={{ color: '#64748B' }}>Manage all bookings</p>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2"
              style={{ color: '#64748B' }}
            />
            <input
              type="text"
              placeholder="Search by name, email, or booking ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
              style={{ background: 'white' }}
              data-testid="search-bookings"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p style={{ color: '#64748B' }}>Loading bookings...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ background: '#F9F8F6' }}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Booking ID</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Guest</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Check-in</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Check-out</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Payment</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking, index) => (
                    <motion.tr
                      key={booking.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-t border-gray-100"
                      data-testid={`booking-row-${index}`}
                    >
                      <td className="px-6 py-4">
                        <span className="mono text-sm font-medium" style={{ color: '#1A202C' }}>{booking.id.slice(0, 8)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium" style={{ color: '#1A202C' }}>{booking.guest_name}</p>
                          <p className="text-sm" style={{ color: '#64748B' }}>{booking.guest_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#1A202C' }}>{formatDate(booking.check_in)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#1A202C' }}>{formatDate(booking.check_out)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium mono" style={{ color: '#1A202C' }}>₹{booking.total_amount}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: `${getPaymentStatusColor(booking.payment_status)}20`,
                            color: getPaymentStatusColor(booking.payment_status),
                          }}
                        >
                          {booking.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            background: `${getStatusColor(booking.booking_status)}20`,
                            color: getStatusColor(booking.booking_status),
                          }}
                        >
                          {booking.booking_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {booking.booking_status === 'confirmed' && (
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'completed')}
                            className="text-sm font-medium hover:underline"
                            style={{ color: '#1A3C34' }}
                            data-testid={`complete-booking-${index}`}
                          >
                            Complete
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredBookings.length === 0 && (
              <div className="text-center py-20">
                <p style={{ color: '#64748B' }}>No bookings found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;